/**
 * deriveInterpVerdict — shared inter-client interpolation-health verdict from
 * the `/api/diag/snapshot-stats` payload (the SnapshotStatsTracker server
 * aggregate).
 *
 * SINGLE SOURCE OF TRUTH for the netcode-smoothness thresholds. Consumed by
 * BOTH the `/diagnostic` SmoothnessTab and the in-game SmoothnessDashboard
 * overlay. (Dev-tools modernization arc, 2026-05-29 — the derivation had been
 * copied into both surfaces, a dup-hotspot drift risk; extracted here so the
 * thresholds live in one place.)
 *
 * Pure function: feed it the parsed JSON; no fetch, no React.
 */

export type InterpSeverity = 'healthy' | 'mild' | 'severe' | 'unknown';

export interface InterpTypeRow {
  type: 'player' | 'mob';
  effectiveHz: number;
  extrapolatedPct: number;
  hitchedPct: number;
  bufferDepth: number;
  samples: number;
}

export interface InterpVerdict {
  severity: InterpSeverity;
  summary: string;
  detail: string;
  interp: InterpTypeRow[];
  worstExtrapolatedPct: number;
  worstHitchedPct: number;
  anySamples: boolean;
}

export function deriveInterpVerdict(snapStats: any): InterpVerdict {
  const interp: InterpTypeRow[] = (snapStats?.perType ?? []).map((p: any) => ({
    type: p.type,
    effectiveHz: p.meanEffectiveHz ?? 0,
    extrapolatedPct: (p.worstExtrapolatedFraction ?? 0) * 100,
    hitchedPct: (p.worstHitchedFraction ?? 0) * 100,
    bufferDepth: p.meanBufferDepth ?? 0,
    samples: p.totalSampleCount ?? 0,
  }));
  const worstExtrap = interp.reduce((m, r) => Math.max(m, r.extrapolatedPct), 0);
  const worstHitch = interp.reduce((m, r) => Math.max(m, r.hitchedPct), 0);
  const anySamples = interp.some((r) => r.samples > 0);

  // Thresholds: extrapolation >25% (or any hitching >10%) means renderTime
  // routinely outruns the buffer → choppy. 10-25% is marginal.
  let severity: InterpSeverity = 'unknown';
  if (anySamples) {
    if (worstExtrap > 25 || worstHitch > 10) severity = 'severe';
    else if (worstExtrap > 10 || worstHitch > 2) severity = 'mild';
    else severity = 'healthy';
  }

  return {
    severity,
    summary: anySamples
      ? `interp: ${worstExtrap.toFixed(0)}% extrapolated · ${worstHitch.toFixed(0)}% hitched`
      : 'no interp samples — enable ?snapstats=1 + a 2nd entity',
    detail: anySamples
      ? interp.map((r) => `${r.type} ${r.effectiveHz.toFixed(0)}Hz/depth${r.bufferDepth.toFixed(1)}`).join(' · ')
      : 'SnapshotStatsTracker reports remote player + mob interpolation health',
    interp,
    worstExtrapolatedPct: worstExtrap,
    worstHitchedPct: worstHitch,
    anySamples,
  };
}
